import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { openStudentHoursPrint } from '../printStudentHours';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

// 평가결과 표에 보여줄 과목: 심화 과목은 제외하고, 최종시험은 맨 아래 한 줄로 따로 보여준다
export const EXCLUDE_SUBCODES = ['EADV'];
export const FINAL_SUBCODE = 'TEXM';
export const subcodeOf = (amtcode) => amtcode.slice(2, 6);

// 화면/PDF에 보여줄 숫자: 0이거나 값이 없으면 빈칸
export function hv(v) {
  if (v === null || v === undefined || Number(v) === 0) return '';
  const n = Number(v);
  return Number.isInteger(n) ? n : n.toFixed(1);
}

export function sumHours(student, chapterList) {
  return chapterList.reduce(
    (acc, c) => {
      const h = student.hours[c.amtcode] || { thrytime: 0, practime: 0 };
      acc.thry += h.thrytime || 0;
      acc.prac += h.practime || 0;
      return acc;
    },
    { thry: 0, prac: 0 }
  );
}

export function pctText(done, legal) {
  if (!legal || legal <= 0) return '';
  const p = Math.min(100, Math.round((done / legal) * 100));
  return p === 0 ? '' : `${p}%`;
}

// 한 학생의 평가 기록(records)을 과목별 표 행으로 만든다.
// 같은 과목/구분(학과 T, 실기 P)의 기록은 평가일자 순으로 1회, 2회, ... 로 센다. 최소 minRows 줄은 빈 줄로라도 둔다.
export function buildExamRows(records, amtcode, minRows) {
  const byType = { T: [], P: [] };
  records
    .filter((r) => r.amtcode === amtcode)
    .sort((a, b) => String(a.exmdate).localeCompare(String(b.exmdate)) || a.exmid - b.exmid)
    .forEach((r) => {
      if (byType[r.tptype]) byType[r.tptype].push(r);
    });
  const n = Math.max(minRows, byType.T.length, byType.P.length);
  return Array.from({ length: n }, (_, i) => ({
    no: i + 1,
    tDate: byType.T[i]?.exmdate || '',
    tScore: byType.T[i]?.score ?? '',
    pDate: byType.P[i]?.exmdate || '',
    pScore: byType.P[i]?.score ?? '',
  }));
}

// 교육결과종합: 학생 한 명의 과목별 시수(학생시수현황 기준)와 평가점수를 한 화면에 보여주고 PDF로 출력한다.
export default function ExamSummary() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [grade, setGrade] = useState(''); // 처음에는 학년/반을 비워 둔다
  const [classtype, setClasstype] = useState('M');
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [studid, setStudid] = useState('');
  const [report, setReport] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/exam-subjects').then((res) => setSubjects(res.data));
  }, []);

  // 학년/구분이 바뀌면 반 목록을 새로 불러오고 반은 빈칸으로 되돌린다 (자동으로 첫 반을 고르지 않는다)
  useEffect(() => {
    setClassNo('');
    if (!grade) {
      setClassOptions([]);
      return undefined;
    }
    let cancelled = false;
    api.get('/classes', { params: { grade, classtype } }).then((res) => {
      if (cancelled) return;
      setClassOptions(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [grade, classtype]);

  // 반이 바뀌면 시수 보고서(학생 명단 포함)와 평가 기록을 다시 불러온다
  useEffect(() => {
    setStudid('');
    setReport(null);
    setRecords([]);
    if (!grade || !classNo) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get('/admin/student-hours-report', { params: { year, grade, classtype, class_no: Number(classNo) } }),
      api.get('/exam-results', { params: { year, classtype, grade, class_no: Number(classNo) } }),
    ])
      .then(([hoursRes, examRes]) => {
        if (cancelled) return;
        setReport(hoursRes.data);
        setRecords(examRes.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, grade, classtype, classNo]);

  const students = report?.students ?? [];
  const student = students.find((s) => s.studid === studid) || null;

  const subjectGroups = useMemo(() => {
    const groups = [];
    (report?.chapters ?? []).forEach((c) => {
      let g = groups[groups.length - 1];
      if (!g || g.subject !== c.group_subject) {
        g = { subject: c.group_subject, chapters: [], legalThry: 0, legalPrac: 0 };
        groups.push(g);
      }
      g.chapters.push(c);
      g.legalThry += c.legal_thrytime || 0;
      g.legalPrac += c.legal_practime || 0;
    });
    return groups;
  }, [report]);

  const hourGroups = useMemo(() => {
    if (!student) return [];
    return subjectGroups.map((g) => {
      const sub = sumHours(student, g.chapters);
      return {
        subject: g.subject,
        legalThry: g.legalThry,
        legalPrac: g.legalPrac,
        thry: sub.thry,
        prac: sub.prac,
        rows: g.chapters.map((c) => {
          const h = student.hours[c.amtcode] || { thrytime: 0, practime: 0 };
          return {
            chapter: c.amt_chapter,
            legalThry: c.legal_thrytime || 0,
            legalPrac: c.legal_practime || 0,
            thry: h.thrytime || 0,
            prac: h.practime || 0,
          };
        }),
      };
    });
  }, [student, subjectGroups]);

  const totals = {
    legalThry: report?.legal_total_thrytime ?? 0,
    legalPrac: report?.legal_total_practime ?? 0,
    thry: student?.total_thrytime || 0,
    prac: student?.total_practime || 0,
  };

  // 평가결과 표: 일반 과목은 2회분 줄, 최종시험은 1줄 (기록이 더 많으면 그만큼 늘어난다)
  const examTable = useMemo(() => {
    if (!student) return [];
    const mine = records.filter((r) => r.studid === student.studid);
    return subjects
      .filter((s) => !EXCLUDE_SUBCODES.includes(subcodeOf(s.amtcode)))
      .map((s) => {
        const isFinal = subcodeOf(s.amtcode) === FINAL_SUBCODE;
        return { subject: s.subject, isFinal, rows: buildExamRows(mine, s.amtcode, isFinal ? 1 : 2) };
      })
      .sort((a, b) => Number(a.isFinal) - Number(b.isFinal));
  }, [student, records, subjects]);

  const info = student
    ? {
        year,
        grade,
        classLabel: `${classtype === 'M' ? '면장반' : '정규반'} ${classNo}반`,
        student: { studid: student.studid, name: student.name },
      }
    : null;

  const handlePrintPdf = () => {
    if (!student) return;
    openStudentHoursPrint(info, hourGroups, totals, examTable);
  };

  return (
    <div className="page exam-summary-page">
      <div className="page-header">
        <h2>교육결과종합</h2>
        <button
          className="btn-ghost"
          type="button"
          onClick={handlePrintPdf}
          disabled={!student}
          title={student ? '선택한 학생의 교육결과종합을 PDF로 저장/인쇄합니다' : '반과 학생을 선택하면 출력할 수 있습니다'}
        >
          PDF 파일 출력
        </button>
      </div>

      <div className="card">
        <div className="form-row es-filter-row">
          <div className="form-field form-field-narrow">
            <label>연도</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}년</option>
              ))}
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>구분</label>
            <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
              <option value="R">정규반</option>
              <option value="M">면장반</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>학년</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value=""></option>
              <option value={1}>1학년</option>
              <option value={2}>2학년</option>
              <option value={3}>3학년</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>반</label>
            <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
              <option value=""></option>
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}반</option>
              ))}
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>학생</label>
            <select value={studid} onChange={(e) => setStudid(e.target.value)}>
              <option value="">선택</option>
              {students.map((s) => (
                <option key={s.studid} value={s.studid}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        {!student && (
          <div className="es-empty">
            {loading ? '불러오는 중...' : '학년, 반, 학생을 선택하면 과목시수와 평가결과가 나타납니다.'}
          </div>
        )}

        {student && (
          <>
            <div className="es-student-info">
              <span>{year}년</span>
              <span>{info.classLabel}</span>
              <span>{grade}학년</span>
              <span>학번 {student.studid}</span>
              <strong>{student.name}</strong>
            </div>

            <h3 className="es-section-title">과목시수</h3>
            <div className="table-scroll">
              <table className="data-table es-table es-hours-table">
                <colgroup>
                  <col style={{ width: '16%' }} />
                  <col />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2}>과목</th>
                    <th rowSpan={2}>교육내용</th>
                    <th colSpan={2}>법정시수</th>
                    <th colSpan={2}>이수시수</th>
                    <th rowSpan={2}>진도율</th>
                  </tr>
                  <tr>
                    <th>학과</th>
                    <th>실습</th>
                    <th>학과</th>
                    <th>실습</th>
                  </tr>
                </thead>
                <tbody>
                  {hourGroups.map((g) => (
                    <React.Fragment key={g.subject}>
                      {g.rows.map((r, i) => (
                        <tr key={r.chapter + i}>
                          {i === 0 && <td className="es-subject-cell" rowSpan={g.rows.length + 1}>{g.subject}</td>}
                          <td className="es-left">{r.chapter}</td>
                          <td>{hv(r.legalThry)}</td>
                          <td>{hv(r.legalPrac)}</td>
                          <td>{hv(r.thry)}</td>
                          <td>{hv(r.prac)}</td>
                          <td>{pctText(r.thry + r.prac, r.legalThry + r.legalPrac)}</td>
                        </tr>
                      ))}
                      <tr className="es-subtotal-row">
                        <td className="es-left">소계</td>
                        <td>{hv(g.legalThry)}</td>
                        <td>{hv(g.legalPrac)}</td>
                        <td>{hv(g.thry)}</td>
                        <td>{hv(g.prac)}</td>
                        <td>{pctText(g.thry + g.prac, g.legalThry + g.legalPrac)}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr className="es-total-row">
                    <td colSpan={2}>시수계</td>
                    <td>{hv(totals.legalThry)}</td>
                    <td>{hv(totals.legalPrac)}</td>
                    <td>{hv(totals.thry)}</td>
                    <td>{hv(totals.prac)}</td>
                    <td>{pctText(totals.thry + totals.prac, totals.legalThry + totals.legalPrac)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="es-section-title">평가결과</h3>
            <div className="table-scroll">
              <table className="data-table es-table es-exam-table">
                <colgroup>
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '13%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2}>과목</th>
                    <th rowSpan={2}>회수</th>
                    <th colSpan={2}>학과</th>
                    <th colSpan={2}>실기</th>
                  </tr>
                  <tr>
                    <th>일자</th>
                    <th>점수</th>
                    <th>일자</th>
                    <th>점수</th>
                  </tr>
                </thead>
                <tbody>
                  {examTable.map((g) =>
                    g.rows.map((r, i) => (
                      <tr key={g.subject + i}>
                        {i === 0 && (
                          <td className="es-subject-cell" rowSpan={g.rows.length} colSpan={g.isFinal ? 2 : 1}>
                            {g.subject}
                          </td>
                        )}
                        {!g.isFinal && <td>{r.no}</td>}
                        <td>{r.tDate}</td>
                        <td>{r.tScore}</td>
                        <td>{r.pDate}</td>
                        <td>{r.pScore}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
